import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { api, type LocalesResponse, type BulkEvent } from "../api";
import { useToast } from "../components/Toast";
import type { Category, Entry, LocaleCode } from "../../shared/types";

type DraftKey = `${string}|${string}`;
type Drafts = Record<DraftKey, { title?: string; capital?: string }>;

const LANG_DISPLAY = (() => {
  try {
    return new Intl.DisplayNames(["en"], { type: "language" });
  } catch {
    return null;
  }
})();
function localeName(code: LocaleCode): string {
  return LANG_DISPLAY?.of(code) ?? code;
}

function LocalePicker({
  locales,
  defaultLocale,
  selected,
  onChange,
}: {
  locales: LocaleCode[];
  defaultLocale: LocaleCode;
  selected: LocaleCode[];
  onChange: (next: LocaleCode[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const candidates = useMemo(
    () => locales.filter((l) => l !== defaultLocale),
    [locales, defaultLocale]
  );

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const label = (() => {
    if (selected.length === 0) return "Select locale…";
    if (candidates.length > 0 && selected.length === candidates.length) {
      return `All locales (${candidates.length})`;
    }
    if (selected.length === 1) return selected[0].toUpperCase();
    if (selected.length === 2) {
      return selected.map((s) => s.toUpperCase()).join(", ");
    }
    return `${selected[0].toUpperCase()} +${selected.length - 1} more`;
  })();

  function toggle(code: LocaleCode) {
    const next = selected.includes(code)
      ? selected.filter((s) => s !== code)
      : candidates.filter((c) => selected.includes(c) || c === code);
    onChange(next);
  }
  function selectAll() {
    onChange([...candidates]);
  }
  function clear() {
    onChange([]);
  }

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        data-testid="locale-picker-trigger"
        onClick={() => setOpen((o) => !o)}
        className="px-2 py-1 rounded border border-gh-border text-sm"
      >
        {label}
      </button>
      {open && (
        <div className="absolute z-30 mt-1 bg-gh-surface border border-gh-border rounded shadow min-w-[16rem] max-h-72 overflow-y-auto">
          <ul>
            <li className="border-b border-gh-border">
              <label className="flex items-center gap-2 px-2 py-1 hover:bg-gh-bg text-sm cursor-pointer font-semibold">
                <input
                  type="checkbox"
                  data-testid="locale-picker-option-all"
                  checked={candidates.length > 0 && selected.length === candidates.length}
                  onChange={(e) => (e.target.checked ? selectAll() : clear())}
                />
                <span className="font-mono text-xs uppercase w-6">—</span>
                <span>All locales</span>
              </label>
            </li>
            {candidates.map((code) => (
              <li key={code}>
                <label className="flex items-center gap-2 px-2 py-1 hover:bg-gh-bg text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`locale-picker-option-${code}`}
                    checked={selected.includes(code)}
                    onChange={() => toggle(code)}
                  />
                  <span className="font-mono text-xs uppercase w-6">{code}</span>
                  <span>{localeName(code)}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function Localizations() {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [cats, setCats] = useState<Category[]>([]);
  const [cfg, setCfg] = useState<LocalesResponse | null>(null);
  const [selected, setSelected] = useState<LocaleCode[]>([]);
  const [catFilter, setCatFilter] = useState<string>("");
  const [showUntranslatedOnly, setShowUntranslatedOnly] = useState(false);
  const [drafts, setDrafts] = useState<Drafts>({});
  const debouncers = useRef<Record<DraftKey, ReturnType<typeof setTimeout>>>({});
  const toast = useToast();
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiTouched, setAiTouched] = useState<Set<string>>(new Set());
  const [bulkProgress, setBulkProgress] = useState<{
    running: boolean;
    locale?: LocaleCode;
    done: number;
    total: number;
    saved: number;
    failedChunks: { locale: LocaleCode; index: number }[];
  } | null>(null);
  const bulkAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    Promise.all([api.listEntries(), api.getLocales(), api.listCategories()]).then(
      ([e, c, cs]) => {
        setEntries(e);
        setCfg(c);
        setCats(cs);
        const firstNonDefault = c.locales.find((l) => l !== c.default);
        setSelected(firstNonDefault ? [firstNonDefault] : []);
      }
    );
    api.aiStatus().then((s) => setAiEnabled(s.enabled)).catch(() => setAiEnabled(false));
  }, []);

  useEffect(() => {
    return () => {
      for (const t of Object.values(debouncers.current)) clearTimeout(t);
    };
  }, []);

  const visible = useMemo(() => {
    const cat = catFilter ? cats.find((c) => c.cat_id === catFilter) : null;
    const allowed = cat ? new Set(cat.countries) : null;
    return entries.filter((e) => {
      if (allowed && !allowed.has(e.id)) return false;
      if (showUntranslatedOnly && selected.length > 0) {
        const hasGap = selected.some((loc) => {
          const t = e.titles[loc]?.trim() ?? "";
          const c = e.capitals[loc]?.trim() ?? "";
          return !t || !c;
        });
        if (!hasGap) return false;
      }
      return true;
    });
  }, [entries, selected, showUntranslatedOnly, catFilter, cats]);

  const coverage = useMemo(() => {
    if (selected.length === 0) return null;
    let full = 0;
    for (const e of entries) {
      const ok = selected.every((loc) => {
        const t = e.titles[loc]?.trim() ?? "";
        const c = e.capitals[loc]?.trim() ?? "";
        return t && c;
      });
      if (ok) full++;
    }
    return { full, total: entries.length };
  }, [entries, selected]);

  function scheduleSave(
    id: string,
    locale: LocaleCode,
    draft: { title?: string; capital?: string }
  ) {
    const key: DraftKey = `${id}|${locale}`;
    if (debouncers.current[key]) clearTimeout(debouncers.current[key]);
    debouncers.current[key] = setTimeout(async () => {
      try {
        const body: { titles?: Record<string, string>; capitals?: Record<string, string> } = {};
        if (draft.title !== undefined) body.titles = { [locale]: draft.title };
        if (draft.capital !== undefined) body.capitals = { [locale]: draft.capital };
        await api.putTranslations(id, body);
        toast.show("success", `${id} saved`);
        setEntries((prev) =>
          prev.map((e) => {
            if (e.id !== id) return e;
            const titles = { ...e.titles };
            const capitals = { ...e.capitals };
            if (draft.title !== undefined) {
              if (draft.title.trim() === "") delete titles[locale];
              else titles[locale] = draft.title;
            }
            if (draft.capital !== undefined) {
              if (draft.capital.trim() === "") delete capitals[locale];
              else capitals[locale] = draft.capital;
            }
            return { ...e, titles, capitals };
          })
        );
        setDrafts((prev) => {
          const n = { ...prev };
          delete n[key];
          return n;
        });
      } catch (err) {
        toast.show("error", (err as Error).message);
      }
    }, 800);
  }

  function getTitleValue(id: string, locale: LocaleCode, e: Entry): string {
    return drafts[`${id}|${locale}`]?.title ?? e.titles[locale] ?? "";
  }
  function getCapitalValue(id: string, locale: LocaleCode, e: Entry): string {
    return drafts[`${id}|${locale}`]?.capital ?? e.capitals[locale] ?? "";
  }

  function rowCoverage(e: Entry): string {
    if (!cfg) return "";
    let n = 0;
    for (const lang of cfg.locales) {
      const t = e.titles[lang]?.trim() ?? "";
      const c = e.capitals[lang]?.trim() ?? "";
      if (t && c) n++;
    }
    return `${n}/${cfg.locales.length}`;
  }

  function aiKey(id: string, locale: LocaleCode, field: "title" | "capital"): string {
    return `${id}|${locale}|${field}`;
  }

  function clearAiHighlight(id: string, locale: LocaleCode, field: "title" | "capital") {
    setAiTouched((prev) => {
      const k = aiKey(id, locale, field);
      if (!prev.has(k)) return prev;
      const n = new Set(prev);
      n.delete(k);
      return n;
    });
  }

  function markAiTouched(id: string, locale: LocaleCode) {
    setAiTouched((prev) => {
      const n = new Set(prev);
      n.add(aiKey(id, locale, "title"));
      n.add(aiKey(id, locale, "capital"));
      return n;
    });
  }

  async function translateRow(id: string) {
    if (selected.length === 0) return;
    const ok = window.confirm(
      `Translate "${id}" to ${selected.map((s) => s.toUpperCase()).join(", ")}?`
    );
    if (!ok) return;
    try {
      const res = await api.aiTranslateRow(id, selected);
      setEntries((prev) => prev.map((e) => (e.id === id ? res.entry : e)));
      for (const s of res.saved) markAiTouched(id, s.locale);
      if (res.failed.length > 0) {
        toast.show(
          "error",
          `Failed: ${res.failed.map((f) => `${f.locale}: ${f.reason}`).join("; ")}`
        );
      } else {
        toast.show("success", `${id} translated`);
      }
    } catch (e) {
      toast.show("error", (e as Error).message);
    }
  }

  function countGaps(): number {
    if (selected.length === 0) return 0;
    let n = 0;
    for (const e of visible) {
      for (const loc of selected) {
        if (!e.titles[loc]?.trim()) n++;
        if (!e.capitals[loc]?.trim()) n++;
      }
    }
    return n;
  }

  async function translateBulk() {
    if (selected.length === 0) return;
    const total = countGaps();
    if (total === 0) {
      toast.show("info", "No empty cells to translate");
      return;
    }
    const ok = window.confirm(
      `Translate ${total} empty cells across ${selected.map((s) => s.toUpperCase()).join(", ")}?`
    );
    if (!ok) return;

    const ids = visible.map((e) => e.id);
    const ctrl = new AbortController();
    bulkAbortRef.current = ctrl;
    setBulkProgress({
      running: true,
      done: 0,
      total,
      saved: 0,
      failedChunks: [],
    });

    try {
      await api.aiTranslateBulk(
        ids,
        selected,
        (ev: BulkEvent) => {
          if (ev.type === "progress") {
            setBulkProgress((prev) =>
              prev
                ? {
                    ...prev,
                    locale: ev.locale,
                    done: prev.done + ev.savedThisChunk,
                    saved: prev.saved + ev.savedThisChunk,
                  }
                : prev
            );
          } else if (ev.type === "chunk_failed") {
            setBulkProgress((prev) =>
              prev
                ? {
                    ...prev,
                    failedChunks: [
                      ...prev.failedChunks,
                      { locale: ev.locale, index: ev.chunkIndex },
                    ],
                  }
                : prev
            );
          } else if (ev.type === "done") {
            setBulkProgress((prev) =>
              prev ? { ...prev, running: false, saved: ev.saved } : prev
            );
            const msg =
              ev.failed === 0
                ? `Translated ${ev.saved} cells`
                : `Translated ${ev.saved} cells; ${ev.failed} failed (${ev.failedChunks
                    .map((c) => `${c.locale} #${c.index}`)
                    .join(", ")})`;
            toast.show(ev.failed === 0 ? "success" : "error", msg);
            api.listEntries().then(setEntries).catch(() => {});
          }
        },
        ctrl.signal
      );
    } catch (e) {
      toast.show("error", (e as Error).message);
      setBulkProgress(null);
    }
  }

  function cancelBulk() {
    bulkAbortRef.current?.abort();
    setBulkProgress(null);
  }

  if (!cfg) return <div className="text-gh-muted">Loading…</div>;

  return (
    <div>
      <div className="flex items-center gap-4 mb-4">
        <label className="text-sm flex items-center gap-2">
          Editing locale:
          <LocalePicker
            locales={cfg.locales}
            defaultLocale={cfg.default}
            selected={selected}
            onChange={setSelected}
          />
        </label>
        {coverage && (
          <span className="text-xs text-gh-muted">
            {coverage.full}/{coverage.total} fully translated
          </span>
        )}
        <select
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          className="px-2 py-1 rounded border border-gh-border text-sm"
        >
          <option value="">All categories</option>
          {cats.map((c) => (
            <option key={c.cat_id} value={c.cat_id}>
              {c.cat_id} ({c.countries.length})
            </option>
          ))}
        </select>
        <label className="text-xs text-gh-muted ml-auto">
          <input
            type="checkbox"
            checked={showUntranslatedOnly}
            onChange={(e) => setShowUntranslatedOnly(e.target.checked)}
            className="mr-1"
          />
          Show untranslated only
        </label>
        {aiEnabled && (
          <button
            type="button"
            data-testid="ai-translate-bulk"
            disabled={selected.length === 0 || !!bulkProgress?.running}
            onClick={translateBulk}
            className="px-2 py-1 rounded border border-gh-border text-sm disabled:opacity-40"
          >
            ✨ Translate all gaps
          </button>
        )}
      </div>

      {bulkProgress && (
        <div
          data-testid="bulk-banner"
          className="mb-2 px-3 py-2 rounded border border-blue-500/40 bg-blue-500/10 text-sm flex items-center gap-3"
        >
          <span>
            {bulkProgress.running
              ? `Translating ${bulkProgress.locale?.toUpperCase() ?? "…"}: ${bulkProgress.done}/${bulkProgress.total}`
              : `Done — ${bulkProgress.saved}/${bulkProgress.total} cells`}
          </span>
          {bulkProgress.failedChunks.length > 0 && (
            <span className="text-red-400 text-xs">
              {bulkProgress.failedChunks.length} chunk(s) failed
            </span>
          )}
          {bulkProgress.running && (
            <button
              type="button"
              onClick={cancelBulk}
              className="ml-auto px-2 py-0.5 rounded border border-gh-border text-xs"
            >
              Cancel
            </button>
          )}
          {!bulkProgress.running && (
            <button
              type="button"
              onClick={() => setBulkProgress(null)}
              className="ml-auto px-2 py-0.5 rounded border border-gh-border text-xs"
            >
              Dismiss
            </button>
          )}
        </div>
      )}

      <div className="overflow-x-auto border border-gh-border rounded">
        <table className="bg-gh-surface text-sm">
          <thead className="bg-gh-bg text-left text-xs text-gh-muted">
            <tr>
              <th
                className="px-3 py-2 sticky left-0 z-20 bg-gh-bg"
                style={{ width: 56, minWidth: 56 }}
              ></th>
              <th
                className="px-3 py-2 sticky z-20 bg-gh-bg"
                style={{ left: 56 }}
              >
                id
              </th>
              <th className="px-3 py-2 min-w-[5rem]">Locales</th>
              <th className="px-3 py-2 min-w-[12rem]">{cfg.default.toUpperCase()} title</th>
              <th className="px-3 py-2 min-w-[10rem]">{cfg.default.toUpperCase()} capital</th>
              {selected.map((loc) => (
                <Fragment key={loc}>
                  <th className="px-3 py-2 min-w-[12rem]">{loc.toUpperCase()} title</th>
                  <th className="px-3 py-2 min-w-[10rem]">{loc.toUpperCase()} capital</th>
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((e) => (
              <tr key={e.id} className="border-t border-gh-border">
                <td
                  className="px-3 py-2 sticky left-0 z-10 bg-gh-surface"
                  style={{ width: 56, minWidth: 56 }}
                >
                  <img src={`/${e.images.flag}`} alt="" className="w-8 h-6 object-contain" />
                </td>
                <td
                  className="px-3 py-2 font-mono text-xs sticky z-10 bg-gh-surface"
                  style={{ left: 56 }}
                >
                  <span className="inline-flex items-center gap-2">
                    <span>{e.id}</span>
                    {aiEnabled && (
                      <button
                        type="button"
                        data-testid={`ai-translate-row-${e.id}`}
                        disabled={selected.length === 0}
                        onClick={() => translateRow(e.id)}
                        title={
                          selected.length === 0
                            ? "Select a target locale first"
                            : "Translate row with AI"
                        }
                        className="px-1 rounded text-xs disabled:opacity-40"
                      >
                        ✨
                      </button>
                    )}
                  </span>
                </td>
                <td className="px-3 py-2 text-gh-muted text-xs">{rowCoverage(e)}</td>
                <td className="px-3 py-2 text-gh-muted">{e.titles[cfg.default]}</td>
                <td className="px-3 py-2 text-gh-muted">{e.capitals[cfg.default]}</td>
                {selected.map((loc) => (
                  <Fragment key={loc}>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        data-testid={`title-${loc}-${e.id}`}
                        value={getTitleValue(e.id, loc, e)}
                        onChange={(ev) => {
                          const k: DraftKey = `${e.id}|${loc}`;
                          const next = { ...drafts[k], title: ev.target.value };
                          setDrafts((prev) => ({ ...prev, [k]: next }));
                          clearAiHighlight(e.id, loc, "title");
                          scheduleSave(e.id, loc, next);
                        }}
                        className={
                          "w-full px-2 py-1 rounded border text-sm " +
                          (aiTouched.has(aiKey(e.id, loc, "title"))
                            ? "border-blue-500/60 ring-1 ring-blue-500/40"
                            : "border-gh-border")
                        }
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        data-testid={`capital-${loc}-${e.id}`}
                        value={getCapitalValue(e.id, loc, e)}
                        onChange={(ev) => {
                          const k: DraftKey = `${e.id}|${loc}`;
                          const next = { ...drafts[k], capital: ev.target.value };
                          setDrafts((prev) => ({ ...prev, [k]: next }));
                          clearAiHighlight(e.id, loc, "capital");
                          scheduleSave(e.id, loc, next);
                        }}
                        className={
                          "w-full px-2 py-1 rounded border text-sm " +
                          (aiTouched.has(aiKey(e.id, loc, "capital"))
                            ? "border-blue-500/60 ring-1 ring-blue-500/40"
                            : "border-gh-border")
                        }
                      />
                    </td>
                  </Fragment>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
